import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Text } from '@/components/ui/text';
import { useProjectsService } from '@/services/projects';
import { FolderOpen } from 'lucide-react';
import { FC, useMemo, useState } from 'react';

export const MoveToProjectModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (projectId: string) => void;
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
}> = ({
  open,
  onOpenChange,
  onSelect,
  title = 'Add to project',
  subtitle = 'Select a project',
  isLoading = false,
}) => {
  const [search, setSearch] = useState('');
  const { projects } = useProjectsService();

  const filteredProjects = useMemo(() => {
    if (!search.trim()) return projects;
    const term = search.toLowerCase();
    return projects.filter(p => p.name.toLowerCase().includes(term));
  }, [projects, search]);

  const handleSelect = (projectId: string) => {
    onSelect(projectId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Search projects..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <ScrollArea className="max-h-[300px]">
          {filteredProjects.length === 0 ? (
            <div className="py-6 text-center">
              <Text variant="body-sm" color="secondary">
                No projects found
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {filteredProjects.map(project => (
                <Button
                  key={project.id}
                  variant="ghost"
                  className="justify-start gap-2 w-full"
                  onClick={() => handleSelect(project.id)}
                  disabled={isLoading}
                >
                  <FolderOpen className="h-4 w-4 shrink-0" />
                  <Text variant="body-sm" color="primary" className="truncate">
                    {project.name}
                  </Text>
                </Button>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogClose asChild>
          <Button variant="outline" className="w-full">
            Cancel
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};
